import React, { useMemo, useState } from 'react';
import { TrendingUp, FileText, Sparkles, Layers, Building2, BarChart3, Download, RotateCcw } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import { downloadBanfExport } from 'core/api/apiService';
import MultiSelectDropdown from '../core/ui/MultiSelectDropdown';

/**
 * ProcurementKpiDashboard — Beschaffungs-Kennzahlen (F14).
 *
 * KPIs werden CLIENT-SEITIG aus den bereits geladenen, rollen-gescopten
 * purchase_requests berechnet. Filter (Mehrfachauswahl + suchbar, via gemeinsamer
 * MultiSelectDropdown) an echten EBAN-Feldern orientiert. Excel-Export über den
 * Backend-Endpoint (ExcelJS) mit denselben Filtern.
 */

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const fmtEur0 = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
const itemEkgrp = (it) => String(it.einkauefergruppe || it.einkaeufergruppe || it.purchasing_group || '');
const itemMatkl = (it) => String(it.material_group || it.product_group || it.category || '');

// Akzentfarben je Kachel (Hell + Dark).
const ACCENTS = {
  blue:    { chip: 'bg-blue-100 text-blue-600',       chipDark: 'bg-blue-500/20 text-blue-300',       bar: 'bg-blue-500' },
  emerald: { chip: 'bg-emerald-100 text-emerald-600', chipDark: 'bg-emerald-500/20 text-emerald-300', bar: 'bg-emerald-500' },
  violet:  { chip: 'bg-violet-100 text-violet-600',   chipDark: 'bg-violet-500/20 text-violet-300',   bar: 'bg-violet-500' },
  amber:   { chip: 'bg-amber-100 text-amber-600',     chipDark: 'bg-amber-500/20 text-amber-300',     bar: 'bg-amber-500' },
};

const EMPTY = { year: [], status: [], ekgrp: [], matkl: [], kostl: [] };

const ProcurementKpiDashboard = ({ purchaseRequests, onOpenRequests, user }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const reqs = Array.isArray(purchaseRequests) ? purchaseRequests : [];

  const [filters, setFilters] = useState(EMPTY);
  const [exporting, setExporting] = useState(false);

  const statusLabel = (s) => ({ pending: t('kpi.statusPending'), approved: t('kpi.statusApproved'), rejected: t('kpi.statusRejected') }[s] || s);

  // Filter-Optionen aus den Rohdaten
  const options = useMemo(() => {
    const years = new Set(), statuses = new Set(), ekgrps = new Set(), matkls = new Set(), kostls = new Set();
    reqs.forEach((r) => {
      const d = r.created_at ? new Date(r.created_at) : null;
      if (d && !isNaN(d.getTime())) years.add(String(d.getFullYear()));
      if (r.status) statuses.add(r.status);
      if (r.requester_cost_center) kostls.add(String(r.requester_cost_center));
      (Array.isArray(r.items) ? r.items : []).forEach((it) => {
        if (itemEkgrp(it)) ekgrps.add(itemEkgrp(it));
        if (itemMatkl(it)) matkls.add(itemMatkl(it));
      });
    });
    return {
      years: [...years].sort().reverse(),
      statuses: [...statuses].sort().map((s) => ({ value: s, label: statusLabel(s) })),
      ekgrps: [...ekgrps].sort(),
      matkls: [...matkls].sort(),
      kostls: [...kostls].sort(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqs]);

  // Filter anwenden (leeres Array = alle)
  const filtered = useMemo(() => reqs.filter((r) => {
    if (filters.status.length && !filters.status.includes(r.status)) return false;
    if (filters.kostl.length && !filters.kostl.includes(String(r.requester_cost_center))) return false;
    if (filters.year.length) {
      const d = r.created_at ? new Date(r.created_at) : null;
      if (!d || isNaN(d.getTime()) || !filters.year.includes(String(d.getFullYear()))) return false;
    }
    const items = Array.isArray(r.items) ? r.items : [];
    if (filters.ekgrp.length && !items.some((it) => filters.ekgrp.includes(itemEkgrp(it)))) return false;
    if (filters.matkl.length && !items.some((it) => filters.matkl.includes(itemMatkl(it)))) return false;
    return true;
  }), [reqs, filters]);

  const kpi = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    let ytd = 0, mtd = 0, total = 0;
    const byStatus = {}; const byGroup = {}; const byCC = {}; const byMonth = {};
    filtered.forEach((r) => {
      const amt = num(r.total_amount);
      total += amt;
      const d = r.created_at ? new Date(r.created_at) : null;
      if (d && !isNaN(d.getTime())) {
        if (d.getFullYear() === year) ytd += amt;
        if (d.getFullYear() === year && d.getMonth() === month) mtd += amt;
        const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        byMonth[mk] = (byMonth[mk] || 0) + amt;
      }
      byStatus[r.status || 'unknown'] = (byStatus[r.status || 'unknown'] || 0) + 1;
      const cc = r.requester_cost_center || '—';
      byCC[cc] = (byCC[cc] || 0) + amt;
      (Array.isArray(r.items) ? r.items : []).forEach((it) => {
        const g = itemMatkl(it) || t('kpi.groupOther');
        byGroup[g] = (byGroup[g] || 0) + (num(it.price) * num(it.quantity));
      });
    });
    const count = filtered.length;
    const avg = count ? total / count : 0;
    const topGroups = Object.entries(byGroup).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).slice(0, 5);
    const topCC = Object.entries(byCC).filter(([k, v]) => v > 0 && k !== '—').sort((a, b) => b[1] - a[1]).slice(0, 5);
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const dd = new Date(year, month - i, 1);
      const mk = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}`;
      months.push({ label: dd.toLocaleDateString('de-DE', { month: 'short' }), val: byMonth[mk] || 0 });
    }
    return { ytd, mtd, total, count, avg, byStatus, topGroups, topCC, months, maxMonth: Math.max(1, ...months.map((m) => m.val)) };
  }, [filtered, t]);

  const cardCls = isDarkMode ? 'bg-gray-900/40 border border-white/15 backdrop-blur-xl rounded-2xl' : 'glass-light-card';
  const headingCls = isDarkMode ? 'text-white' : 'text-slate-900';
  const subCls = isDarkMode ? 'text-white/60' : 'text-slate-500';
  const valCls = isDarkMode ? 'text-white' : 'text-slate-900';

  const hasFilter = Object.values(filters).some((a) => a.length > 0);
  const setF = (k) => (next) => setFilters((f) => ({ ...f, [k]: next }));
  const reset = () => setFilters(EMPTY);
  const bulkProps = { selectAllLabel: t('kpi.selectAll'), clearAllLabel: t('kpi.clearAll'), searchPlaceholder: t('kpi.search') };

  const handleExport = async () => {
    if (exporting || !user) return;
    setExporting(true);
    try { await downloadBanfExport(user, filters); } catch (_e) { /* Download-Fehler ist im Browser sichtbar */ }
    finally { setExporting(false); }
  };

  const StatCard = ({ icon: Icon, label, value, hint, accent, onClick }) => {
    const a = ACCENTS[accent] || ACCENTS.blue;
    return (
      <button type="button" onClick={onClick} className={`${cardCls} p-5 text-left relative overflow-hidden transition-transform ${onClick ? 'hover:-translate-y-0.5 cursor-pointer' : 'cursor-default'}`}>
        <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${a.bar}`} />
        <div className="flex items-center gap-2 mb-2 pl-1">
          <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? a.chipDark : a.chip}`}><Icon className="w-4 h-4" /></span>
          <span className={`text-xs ${subCls}`}>{label}</span>
        </div>
        <div className={`text-2xl font-bold pl-1 ${valCls}`}>{value}</div>
        {hint ? <div className={`text-xs mt-1 pl-1 ${subCls}`}>{hint}</div> : null}
      </button>
    );
  };

  const st = kpi.byStatus;
  const statusHint = `${t('kpi.statusPending')}: ${st.pending || 0} · ${t('kpi.statusApproved')}: ${st.approved || 0}${st.rejected ? ` · ${t('kpi.statusRejected')}: ${st.rejected}` : ''}`;

  // dezenter Button (gleiche Optik wie die Filter-Chips)
  const subtleBtn = `inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${isDarkMode ? 'bg-white/8 border-white/15 text-white/70 hover:text-white hover:border-brand/40' : 'bg-white border-slate-200 text-slate-600 hover:border-brand/40 hover:text-brand'}`;

  return (
    <div className="space-y-6">
      {/* Titel */}
      <div className="flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-brand" />
        <h2 className={`text-lg font-semibold ${headingCls}`}>{t('kpi.title')}</h2>
      </div>

      {/* Filterleiste (Mehrfachauswahl + suchbar) — Export dezent auf derselben Ebene.
          relative z-30: hebt die Leiste (eigener Stacking-Context durch backdrop-blur)
          über die nachfolgenden Kacheln, damit das offene Dropdown davor liegt. */}
      <div className={`${cardCls} p-3 relative z-30`}>
        <div className="flex flex-wrap items-center gap-2">
          <MultiSelectDropdown label={t('kpi.filterYear')} options={options.years} selected={filters.year} onChange={setF('year')} searchable width="w-40" {...bulkProps} />
          <MultiSelectDropdown label={t('kpi.filterStatus')} options={options.statuses} selected={filters.status} onChange={setF('status')} width="w-48" {...bulkProps} />
          <MultiSelectDropdown label={t('kpi.filterEkgrp')} options={options.ekgrps} selected={filters.ekgrp} onChange={setF('ekgrp')} searchable width="w-56" {...bulkProps} />
          <MultiSelectDropdown label={t('kpi.filterMatkl')} options={options.matkls} selected={filters.matkl} onChange={setF('matkl')} searchable width="w-56" {...bulkProps} />
          <MultiSelectDropdown label={t('kpi.filterKostl')} options={options.kostls} selected={filters.kostl} onChange={setF('kostl')} searchable width="w-48" {...bulkProps} />
          {hasFilter && (
            <button type="button" onClick={reset} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${isDarkMode ? 'text-white/60 hover:text-white/90' : 'text-slate-500 hover:text-slate-700'}`}>
              <RotateCcw className="w-3.5 h-3.5" /> {t('kpi.reset')}
            </button>
          )}
          <button type="button" onClick={handleExport} disabled={exporting || reqs.length === 0} className={`${subtleBtn} ml-auto`}>
            <Download className="w-3.5 h-3.5" /> {exporting ? t('kpi.exporting') : t('kpi.export')}
          </button>
        </div>
      </div>

      {reqs.length === 0 ? (
        <div className={`${cardCls} p-12 text-center`}>
          <BarChart3 className={`w-10 h-10 mx-auto mb-3 ${isDarkMode ? 'text-white/40' : 'text-slate-300'}`} />
          <p className={subCls}>{t('kpi.empty')}</p>
        </div>
      ) : (
        <>
          {/* Stat-Karten (farbig) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={TrendingUp} accent="blue" label={t('kpi.valueYtd')} value={fmtEur0(kpi.ytd)} hint={t('kpi.thisYear')} onClick={onOpenRequests} />
            <StatCard icon={TrendingUp} accent="emerald" label={t('kpi.valueMtd')} value={fmtEur0(kpi.mtd)} hint={t('kpi.thisMonth')} onClick={onOpenRequests} />
            <StatCard icon={FileText} accent="violet" label={t('kpi.count')} value={kpi.count} hint={statusHint} onClick={onOpenRequests} />
            <StatCard icon={Sparkles} accent="amber" label={t('kpi.avg')} value={fmtEur0(kpi.avg)} hint={t('kpi.perRequest')} onClick={onOpenRequests} />
          </div>

          {/* 12-Monats-Trend */}
          <div className={`${cardCls} p-5`}>
            <div className={`flex items-center gap-2 text-sm font-semibold ${headingCls} mb-4`}>
              <BarChart3 className="w-4 h-4 text-brand" /> {t('kpi.trend12m')}
            </div>
            <div className="flex items-end gap-2 h-40">
              {kpi.months.map((m, i) => (
                <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
                  <div
                    className="w-full rounded-t bg-gradient-to-t from-blue-600 to-blue-400 hover:from-blue-500 hover:to-blue-300 transition-colors"
                    style={{ height: `${Math.round((m.val / kpi.maxMonth) * 100)}%`, minHeight: m.val > 0 ? '4px' : '0' }}
                    title={fmtEur0(m.val)}
                  />
                  <span className={`text-[10px] ${subCls}`}>{m.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top-Listen */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className={`${cardCls} p-5`}>
              <div className={`flex items-center gap-2 text-sm font-semibold ${headingCls} mb-3`}>
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'}`}><Layers className="w-4 h-4" /></span>
                {t('kpi.topGroups')}
              </div>
              {kpi.topGroups.length === 0 ? <p className={`text-xs ${subCls}`}>{t('kpi.noData')}</p> : (
                <ul className="space-y-2">
                  {kpi.topGroups.map(([g, v]) => (
                    <li key={g} className="flex items-center justify-between text-sm">
                      <span className={isDarkMode ? 'text-white/80' : 'text-slate-700'}>{g}</span>
                      <span className={`font-semibold ${valCls}`}>{fmtEur0(v)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className={`${cardCls} p-5`}>
              <div className={`flex items-center gap-2 text-sm font-semibold ${headingCls} mb-3`}>
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-600'}`}><Building2 className="w-4 h-4" /></span>
                {t('kpi.topCostCenters')}
              </div>
              {kpi.topCC.length === 0 ? <p className={`text-xs ${subCls}`}>{t('kpi.noData')}</p> : (
                <ul className="space-y-2">
                  {kpi.topCC.map(([cc, v]) => (
                    <li key={cc} className="flex items-center justify-between text-sm">
                      <span className={isDarkMode ? 'text-white/80' : 'text-slate-700'}>{cc}</span>
                      <span className={`font-semibold ${valCls}`}>{fmtEur0(v)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProcurementKpiDashboard;
